import { memo } from 'react'
import { Handle, Position, type NodeProps, type Node } from '@xyflow/react'
import { ChevronDown, ChevronRight, Users } from 'lucide-react'
import type { OrgChartNode } from '@/types/hr'
import type { LayoutDir } from './orgChartLayout'
import { countryFlag } from './countryFlags'

export interface OrgNodeData {
  node: OrgChartNode
  directReports: number
  hasChildren: boolean
  collapsed: boolean
  highlighted: boolean
  dimmed: boolean
  dir: LayoutDir
  accent: string
  onToggle: (id: string) => void
  onOpenMenu: (id: string, x: number, y: number) => void
  [key: string]: unknown
}

export type OrgFlowNode = Node<OrgNodeData, 'orgNode'>

// Grackle brand palette — used only for the 4px decorative left-accent bar
// (department colour-coding), so text contrast is unaffected.
// Prussian Blue, Navy, Powder Blue, Pollen, Dark Raspberry, Pistachio.
const PALETTE = ['#072744', '#183E6A', '#A0C1DB', '#FFC857', '#941052', '#AFDBB1']

/** Deterministic accent color per department/group name (border accent only,
 * so text contrast is unaffected — keeps the card WCAG-safe). */
export function accentFor(name: string | null | undefined): string {
  const s = name ?? ''
  let h = 0
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0
  return PALETTE[h % PALETTE.length] ?? '#072744'
}

function initials(first?: string | null, last?: string | null): string {
  return `${(first ?? '').charAt(0)}${(last ?? '').charAt(0)}`.toUpperCase() || '?'
}

function OrgNodeCardImpl({ data }: NodeProps<OrgFlowNode>) {
  const { node, directReports, hasChildren, collapsed, highlighted, dimmed, dir, accent } = data
  const name = `${node.preferred_name || node.first_name} ${node.last_name}`.trim()
  const subtitle = [node.job_title, node.department_name].filter(Boolean).join(' · ')
  const flag = countryFlag(node.country)
  const targetPos = dir === 'LR' ? Position.Left : Position.Top
  const sourcePos = dir === 'LR' ? Position.Right : Position.Bottom

  return (
    <div
      role="group"
      aria-label={`${name}${subtitle ? `, ${subtitle}` : ''}${directReports ? `, ${directReports} direct reports` : ''}`}
      onContextMenu={(e) => {
        e.preventDefault()
        data.onOpenMenu(node.id, e.clientX, e.clientY)
      }}
      className={`relative flex items-center gap-2 rounded-lg border px-3 py-2 shadow-sm transition-opacity ${
        highlighted
          ? 'bg-[#FFC857] border-[#072744] text-[#072744] ring-2 ring-[#072744]'
          : 'bg-card border-border'
      } ${dimmed ? 'opacity-40' : 'opacity-100'}`}
      style={{ width: 240, height: 92, borderLeft: `4px solid ${accent}` }}
    >
      <Handle type="target" position={targetPos} className="!bg-muted-foreground" />
      {node.profile_photo_url ? (
        <img
          src={node.profile_photo_url}
          alt=""
          className="h-10 w-10 flex-shrink-0 rounded-full object-cover bg-muted"
        />
      ) : (
        <div
          className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary"
          aria-hidden="true"
        >
          {initials(node.first_name, node.last_name)}
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1">
          <p className="truncate text-sm font-semibold leading-tight">{name}</p>
          {flag ? (
            <span
              role="img"
              aria-label={flag.label}
              title={flag.label}
              className="shrink-0 text-base leading-none"
            >
              {flag.emoji}
            </span>
          ) : node.country ? (
            <span
              className={`shrink-0 text-[10px] ${highlighted ? 'text-[#072744]' : 'text-muted-foreground'}`}
            >
              {node.country}
            </span>
          ) : null}
        </div>
        {subtitle && (
          <p
            className={`truncate text-xs ${highlighted ? 'text-[#072744]' : 'text-muted-foreground'}`}
          >
            {subtitle}
          </p>
        )}
        {directReports > 0 && (
          <p
            className={`mt-0.5 flex items-center gap-1 text-[11px] ${highlighted ? 'text-[#072744]' : 'text-muted-foreground'}`}
          >
            <Users className="h-3 w-3" aria-hidden="true" />
            {directReports}
          </p>
        )}
      </div>

      {hasChildren && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            data.onToggle(node.id)
          }}
          aria-label={collapsed ? `Expand ${name}'s reports` : `Collapse ${name}'s reports`}
          aria-expanded={!collapsed}
          className="absolute -bottom-3 left-1/2 z-10 flex h-6 w-6 -translate-x-1/2 items-center justify-center rounded-full border border-border bg-background text-foreground shadow hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring"
        >
          {collapsed ? (
            <ChevronRight className="h-4 w-4" aria-hidden="true" />
          ) : (
            <ChevronDown className="h-4 w-4" aria-hidden="true" />
          )}
        </button>
      )}
      <Handle type="source" position={sourcePos} className="!bg-muted-foreground" />
    </div>
  )
}

export const OrgNodeCard = memo(OrgNodeCardImpl)
